import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { membersApi } from '../api'
import { ApprovalStatus } from '../api/models'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import NotApprovedLayout from '../layouts/NotApprovedLayout.vue'

// Extend route meta interface
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    breadcrumb?: string
    parentRoute?: string
    memberName?: string
    allowedRoles?: string[]
  }
}

// import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: () => {
      // This will be handled by the navigation guard to redirect based on role
      return { name: 'dashboard' }
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    meta: {
      requiresAuth: true,
      allowedRoles: ['admin'],
    },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: {
          breadcrumb: 'Dashboard',
          allowedRoles: ['admin'],
        },
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
        meta: {
          breadcrumb: 'Settings',
          parentRoute: 'dashboard',
          allowedRoles: ['admin', 'user'],
        },
      },
      {
        name: 'profile',
        path: 'profile',
        component: () => import('../pages/profile/Profile.vue'),
        meta: {
          breadcrumb: 'Profile',
          parentRoute: 'dashboard',
          allowedRoles: ['admin', 'user'],
        },
      },
      {
        name: 'members',
        path: 'members',
        component: () => import('../pages/members/MembersPage.vue'),
        meta: {
          breadcrumb: 'Members',
          parentRoute: 'dashboard',
          allowedRoles: ['admin'],
        },
      },
      {
        path: 'members/:id',
        name: 'member-detail',
        component: () => import('../pages/members/MemberDetailPage.vue'),
        meta: {
          requiresAuth: true,
          breadcrumb: 'Member Details',
          parentRoute: 'members',
          allowedRoles: ['admin'],
        },
      },
    ],
  },
  {
    name: 'not-approved',
    path: '/approval-pending',
    component: NotApprovedLayout,
    meta: {
      requiresAuth: true,
      allowedRoles: ['admin', 'user'], // Allow both since approval check is done post-login
    },
    children: [
      {
        name: 'approval-pending',
        path: '',
        component: () => import('../pages/approval/ApprovalPending.vue'),
        meta: {
          allowedRoles: ['admin', 'user'],
        },
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

// Add navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const publicPages = ['/auth/login', '/auth/signup', '/auth/recover-password', '/404']
  const authRequired = (!publicPages.includes(to.path) && !to.path.startsWith('/auth/')) || to.meta.requiresAuth

  // Initialize auth state on first navigation
  if (authRequired) {
    authStore.initializeAuth()
  }

  if (authRequired && !authStore.isAuthenticated) {
    // Save intended destination for redirect after login
    const loginPath = '/auth/login'
    const redirect = to.fullPath === '/' ? undefined : to.fullPath
    return next({ path: loginPath, query: redirect ? { redirect } : undefined })
  }

  // Check role-based access
  if (authStore.isAuthenticated && to.meta.allowedRoles) {
    const userRole = authStore.userRole
    if (!userRole || !to.meta.allowedRoles.includes(userRole)) {
      // Redirect based on user role
      return next(getDefaultRouteForRole(userRole))
    }

    // For users, check approval status before allowing access to user routes
    if (userRole === 'user' && to.meta.allowedRoles.includes('user')) {
      // Skip approval check for approval-pending route to avoid infinite redirects
      if (to.name === 'approval-pending' || to.path === '/approval-pending') {
        return next()
      }

      try {
        const userId = authStore.userId
        if (!userId) {
          return next({ name: 'approval-pending' })
        }

        const memberResponse = await membersApi.getMemberById(userId)
        const memberData = memberResponse.data

        // Update stored member details with current data (including approval status)
        authStore.setMemberDetails({
          title: memberData.title,
          memberNo: memberData.memberNo,
          gender: memberData.gender,
          memberProfileImage: memberData.memberProfileImage,
          firstName: memberData.firstName,
          lastName: memberData.lastName,
          emailAddress: memberData.emailAddress,
          mobileNo: memberData.mobileNo,
          approvalStatus: memberData.approvalStatus,
        })

        // Check if member is not approved
        if (
          memberData.approvalStatus === ApprovalStatus.NotApproved ||
          memberData.approvalStatus === ApprovalStatus.Rejected ||
          memberData.approvalStatus === 'NOTAPPROVED' ||
          memberData.approvalStatus === 'REJECTED'
        ) {
          return next({ name: 'approval-pending' })
        }

        // Member is approved, allow navigation
      } catch (error) {
        // On error, redirect to approval pending as safety measure
        return next({ name: 'approval-pending' })
      }
    }
  }

  // Update activity for authenticated users
  if (authStore.isAuthenticated) {
    authStore.updateActivity()
  }

  next()
})

// Helper function to get default route for user role
function getDefaultRouteForRole(role: string | null) {
  const roleRoutes: Record<string, string> = {
    admin: 'dashboard',
    user: 'profile',
  }
  const routeName = roleRoutes[role || ''] || 'login'
  return { name: routeName }
}

// Make router globally available for auth store
;(globalThis as any).__router__ = router

export default router
